import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPositionsAction, executeConvertPositionAction } from "../store/actions/portfolio";
import { openOrderWindow } from "../store/actions/orders";
import { getFormattedPosition } from "../utils/portfolio";
import octopusInstance from "../services/octopus/octopusInstance";
import { getExchangeCode, exchangeSpecificStringifiedNumber } from "../services/exchanges";
import { formatPriceWithPrecision } from "../utils/utilfunction";
import { placeRegularOrder } from "../services/backendAPI";
import { enqueueSnackbar } from "notistack";
import { mapOrderWindowData } from "../services/utilities";
import useTouchInteraction from "./useTouchInteraction";
import { getDeviceId } from "../utils/deviceId";

export const usePositions = () => {
  const positions = useSelector((state) => state.portfolio.arrPositionsData);
  const positionType = useSelector((state) => state.portfolio.positionType);
  const positionMetrics = useSelector((state) => state.portfolio.positionMetrics);
  const positionsUpdate = useSelector((state) => state.portfolio.arrPositionsUpdateData);
  const { userSettings } = useSelector((state) => state.profile);


  console.log("positions", positions, positionsUpdate);
  const [total, setTotal] = useState(0);
  const [totalInvestedValue, setTotalInvestedValue] = useState(0);
  const [totalPnlPercentage, setTotalPnlPercentage] = useState(0);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedPosition, setSelectedPositions] = useState([]);
  const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
  const [conversionDrawerOpen, setConversionDrawerOpen] = useState(false);
  const [selectedPositionForConversion, setSelectedPositionForConversion] = useState(null);
  const [pendingOrdersModalOpen, setPendingOrdersModalOpen] = useState(false);
  const [conflictingOrders, setConflictingOrders] = useState([]);
  const [cancellingOrders, setCancellingOrders] = useState([]);
  const [showConfirmationInModal, setShowConfirmationInModal] = useState(false);

  // Add a ref to track if positions selection has changed
  const positionsSelectionChanged = useRef(false);

  const dispatch = useDispatch();
  const allPendingOrders = useSelector((state) => state.orders.arrPendingsData);
  const getPositionPendingOrders = useCallback((position) => {
    if (!allPendingOrders?.length) return [];
    const positionSide = position.net_quantity > 0 ? 'SELL' : 'BUY';
    return allPendingOrders.filter(order => {
      const isSameInstrument = (String(order.token) === String(position.token) || String(order.instrument_token) === String(position.token)) && String(order.exchange) === String(position.exchange);
      const isSameProduct = order.product === position.product;
      const isPending = order.order_status && !order.order_status.includes('COMPLETED') && !order.order_status.includes('CANCELLED') && !order.order_status.includes('REJECTED');
      const isOppositeOrderSide = order.order_side !== positionSide;
      return isSameInstrument && isSameProduct && isOppositeOrderSide && isPending;
    });
  }, [allPendingOrders]);

  const getSelectedPositionsWithPendingOrders = useCallback(() => {
    return selectedPosition.filter(position => getPositionPendingOrders(position).length > 0);
  }, [selectedPosition, getPositionPendingOrders]);

  const shouldDisplaySquareOff = useCallback((position) => {
    if (!position || position.net_quantity === 0) return false;
    return true;
  }, []);
  const isSquareOffEnabled = useCallback((position) => {
    return shouldDisplaySquareOff(position) && getPositionPendingOrders(position).length === 0;
  }, [shouldDisplaySquareOff, getPositionPendingOrders]);
  
  const getAllConflictingOrdersWithScriptNames = useCallback(() => {
    const allConflictingOrders = [];
    selectedPosition.forEach(position => {
      getPositionPendingOrders(position).forEach(order => {
        allConflictingOrders.push({
          ...order,
          scriptName: position.symbol || position.instrument_name || 'Unknown Script',
          position
        });
      });
    });
    return allConflictingOrders;
  }, [selectedPosition, getPositionPendingOrders]);

  const shouldShowSquareOffAllModal = useCallback(() => {
    if (selectedPosition.length === 0) return false;
    
    // Check if any selected position has conflicting pending orders
    // Matching criteria: product, instrument token, exchange, and opposite order side
    const hasConflictingOrders = selectedPosition.some(position => {
      if (!allPendingOrders?.length) return false;
      
      const positionSide = position.net_quantity > 0 ? 'SELL' : 'BUY';
      return allPendingOrders.some(order => {
        const isSameInstrument = (String(order.token) === String(position.token) || String(order.instrument_token) === String(position.token)) && String(order.exchange) === String(position.exchange);
        const isSameProduct = order.product === position.product;
        const isPending = order.order_status && !order.order_status.includes('COMPLETED') && !order.order_status.includes('CANCELLED') && !order.order_status.includes('REJECTED');
        const isOppositeOrderSide = order.order_side !== positionSide;
        return isSameInstrument && isSameProduct && isOppositeOrderSide && isPending;
      });
    });
    
    return hasConflictingOrders;
  }, [selectedPosition, allPendingOrders]);

  useEffect(() => {
    if (positionsUpdate && positionsUpdate.length > 0) {
      // Only update selected positions if the selection has actually changed
      // or this is the first time we're loading
      if (positionsSelectionChanged.current || selectedPosition.length === 0) {
        const updatedSelectedPositions = positionsUpdate.filter(position =>
          position.isSelected === true
        );

        setSelectedPositions(updatedSelectedPositions);
        // Only count non-zero positions when calculating if all are selected
        setIsAllSelected(updatedSelectedPositions.length === positionsUpdate.filter(item => item.net_quantity !== 0).length);

        // Reset the selection change flag
        positionsSelectionChanged.current = false;
      }
    }
  }, [positionsUpdate]);

  const updatePositionSelection = useCallback((position, isSelected) => {
    // Set the flag to indicate selection has changed
    positionsSelectionChanged.current = true;

    dispatch({
      type: "UPDATE_POSITION_SELECTION",
      payload: {
        position,
        isSelected
      }
    });
  }, [dispatch]);

  const handleSelectAllTap = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      // Only select non-zero positions
      const qtyNotZero = positions.filter((item) => item.net_quantity !== 0);

      // Set the flag to indicate selection has changed
      positionsSelectionChanged.current = true;

      dispatch({
        type: "SET_ALL_POSITIONS_SELECTION",
        payload: !isAllSelected
      });

      if (!isAllSelected) {
        setSelectedPositions(qtyNotZero);
        setIsAllSelected(true);
      } else {
        setSelectedPositions([]);
        setIsAllSelected(false);
      }
    },
    [positions, isAllSelected, dispatch]
  );

  const handleCheckbox = useCallback(
    (e, row) => {
      if (e) {
        e.stopPropagation();
      }

      const isSelectAll = !row;
      const qtyNotZero = positions.filter((item) => item.net_quantity !== 0);

      if (isSelectAll) {
        if (!isAllSelected) {
          // Select all non-zero positions
          setSelectedPositions(qtyNotZero);
          setIsAllSelected(true);

          // Update store for all positions
          qtyNotZero.forEach(position => {
            dispatch({
              type: "UPDATE_POSITION_SELECTION",
              payload: {
                position,
                isSelected: true
              }
            });
          });
        } else {
          // Deselect all
          setSelectedPositions([]);
          setIsAllSelected(false);

          // Update store for all positions
          positions.forEach(position => {
            dispatch({
              type: "UPDATE_POSITION_SELECTION",
              payload: {
                position,
                isSelected: false
              }
            });
          });
        }
        return;
      }

      // Handle individual position selection
      const isSelected = selectedPosition.some(
        (item) =>
          item.token === row.token &&
          item.exchange === row.exchange &&
          item.product === row.product
      );

      // Create new selection array
      let newSelection;
      if (!isSelected) {
        // Add to selection
        newSelection = [...selectedPosition, row];
      } else {
        // Remove from selection
        newSelection = selectedPosition.filter(
          (item) =>
            !(item.token === row.token &&
              item.exchange === row.exchange &&
              item.product === row.product)
        );
      }

      // Update local state first
      setSelectedPositions(newSelection);

      // Update isAllSelected
      setIsAllSelected(newSelection.length === qtyNotZero.length);

      // Update store for the specific position
      dispatch({
        type: "UPDATE_POSITION_SELECTION",
        payload: {
          position: row,
          isSelected: !isSelected
        }
      });
    },
    [positions, selectedPosition, isAllSelected, dispatch]
  );

  const cancelConflictingOrdersForPosition = useCallback(async (position) => {
    const conflictingOrders = getPositionPendingOrders(position);
    if (conflictingOrders.length === 0) return true;

    try {
      const cancelPromises = conflictingOrders.map(async (order) => {
        const loginId = localStorage.getItem("login_id");
        const token = localStorage.getItem("auth_token");

        if (!loginId) {
          throw new Error("User is not authenticated.");
        }

        const response = await fetch(
          `/api/v1/orders/${order.oms_order_id}?client_id=${loginId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "x-authorization-token": token,
              "x-device-type": "WEB",
              "x-device-id": getDeviceId(),
            },
          }
        );

        const data = await response.json();
        if (data.status !== "success") {
          throw new Error(data.message || "Failed to cancel order");
        }

        return order;
      });

      await Promise.all(cancelPromises);
      enqueueSnackbar(`${conflictingOrders.length} conflicting orders cancelled successfully`, { variant: "success" });
      return true;
    } catch (error) {
      enqueueSnackbar(`Failed to cancel conflicting orders: ${error.message}`, { variant: "error" });
      return false;
    }
  }, [getPositionPendingOrders, enqueueSnackbar]);

  const executeSquareOff = useCallback(() => {
    if (selectedPosition.length === 1) {
      const position = selectedPosition[0];
      const orderType = position.net_quantity < 0 ? "BUY" : "SELL";
      dispatch(openOrderWindow(mapOrderWindowData({
        ...position,
        exchange: position.instrument_identifier === "INDICES" ? "NSE" : position.exchange,
        lotsize: Math.abs(position.net_quantity),
        quantity: Math.abs(position.net_quantity),
        order_type: 'MARKET',
        diplayNetQuantity: true,
        isSquareOff: true
      }, orderType, "POSITION", userSettings.order_type, userSettings.product_type)));
    } else {
      try {
        const squareOffPromises = selectedPosition.map((singlePosition) => {
          const payload = {
            client_id: localStorage.getItem("login_id"),
            device: "WEB",
            disclosed_quantity: 0,
            exchange: singlePosition.exchange,
            execution_type: "REGULAR",
            instrument_token: singlePosition.token,
            order_side: Math.sign(singlePosition.net_quantity) === 1 ? "SELL" : "BUY",
            order_type: "MARKET",
            price: 0,
            product: singlePosition.product,
            quantity: Math.abs(singlePosition.net_quantity),
            trigger_price: 0,
            user_order_id: 10002,
            validity: "DAY",
            is_reducing_qty: true,
          };
          return placeRegularOrder(payload)
            .then((res) => {
              enqueueSnackbar(res?.message, { variant: "success" });
              return res;
            })
            .catch((err) => {
              enqueueSnackbar(err, { variant: "error" });
              throw err;
            });
        });

        // Use Promise.allSettled to wait for ALL orders to complete (success or failure)
        Promise.allSettled(squareOffPromises)
          .then((results) => {
            // Count successful and failed orders
            const successful = results.filter(result => result.status === 'fulfilled').length;
            const failed = results.filter(result => result.status === 'rejected').length;

            console.log(`Square-off completed: ${successful} successful, ${failed} failed out of ${selectedPosition.length} total`);

            if (failed === 0) {
              enqueueSnackbar("All positions squared off successfully", { variant: "success" });
            } else if (successful === 0) {
              enqueueSnackbar("All square-off orders failed", { variant: "error" });
            } else {
              enqueueSnackbar(`${successful} positions squared off, ${failed} failed`, { variant: "warning" });
            }
          })
          .finally(() => {
            // All orders are now complete - refresh positions once
            console.log('All square-off orders processed. WebSocket should handle updates...');

            // Add a fallback refresh mechanism in case WebSocket updates fail
            setTimeout(() => {
              console.log('Fallback: Checking if positions were properly updated via WebSocket...');
              // Only refresh if we still have selected positions that should have been cleared
              if (selectedPosition.length > 0) {
                console.log('Fallback: WebSocket update may have failed, forcing position refresh...');
                dispatch(fetchPositionsAction());
              } else {
                console.log('Fallback: Positions were properly updated via WebSocket');
              }
            }, 3000); // Wait 3 seconds for WebSocket updates
          });
      } catch (error) {
        console.error("Square off error:", error);
        enqueueSnackbar(error || "Failed to square off positions", { variant: "error" });
      }
    }

    // Set the flag to indicate selection has changed
    positionsSelectionChanged.current = true;

    setIsAllSelected(false);
    setSelectedPositions([]);
    setOpenConfirmationBox(false);
  }, [selectedPosition, dispatch]);

  const handleSquareOffSingle = useCallback((position) => {
    const orderType = position.net_quantity < 0 ? "BUY" : "SELL";
    dispatch(openOrderWindow(mapOrderWindowData({
      ...position,
      exchange: position.instrument_identifier === "INDICES" ? "NSE" : position.exchange,
      lotsize: Math.abs(position.net_quantity),
      quantity: Math.abs(position.net_quantity),
      order_type: 'MARKET',
      diplayNetQuantity: true,
      isSquareOff: true
    }, orderType, "POSITION", userSettings.order_type, userSettings.product_type)));
  }, [dispatch, userSettings]);

  const handleSquareOffClick = useCallback((e) => {
    if (shouldShowSquareOffAllModal()) {
      // Filter pending orders to only show those conflicting with selected positions
      const filteredConflictingOrders = getAllConflictingOrdersWithScriptNames();
      setConflictingOrders(filteredConflictingOrders);
      setPendingOrdersModalOpen(true);
      return;
    }

    // For single position square-off - go directly to order window
    if (selectedPosition.length === 1) {
      executeSquareOff();
    } else {
      setOpenConfirmationBox(true);
    }
  }, [selectedPosition, executeSquareOff, shouldShowSquareOffAllModal, getAllConflictingOrdersWithScriptNames]);

  const handleConvertClick = useCallback((position) => {
    setSelectedPositionForConversion(position);
    setConversionDrawerOpen(true);
  }, []);

  const handleCloseConversionDrawer = useCallback(() => {
    setConversionDrawerOpen(false);
    setSelectedPositionForConversion(null);
  }, []);

  const handleCancelPendingOrder = useCallback(async (order) => {
    setCancellingOrders(prev => [...prev, order.oms_order_id]);
    try {
      const loginId = localStorage.getItem("login_id");
      const token = localStorage.getItem("auth_token");

      if (!loginId) {
        enqueueSnackbar("User is not authenticated.", { variant: "error" });
        return;
      }

      const response = await fetch(
        `/api/v1/orders/${order.oms_order_id}?client_id=${loginId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-authorization-token": token,
            "x-device-type": "WEB",
            "x-device-id": getDeviceId(),
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {

        setConflictingOrders(prev => prev.filter(o => o.oms_order_id !== order.oms_order_id));
        enqueueSnackbar(data.message || "Order cancelled successfully", { variant: "success" });
      } else {
        enqueueSnackbar(data.message || "Failed to cancel order", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to cancel order: ${error.message}`, { variant: "error" });
    } finally {
      setCancellingOrders(prev => prev.filter(id => id !== order.oms_order_id));
    }
  }, [enqueueSnackbar]);

  const handleClosePendingOrdersModal = useCallback(() => {
    setPendingOrdersModalOpen(false);
    setConflictingOrders([]);
    setCancellingOrders([]);
  }, []);

  const handleCancelAndProceedSquareOff = useCallback(async (order) => {
    setCancellingOrders(prev => [...prev, order.oms_order_id]);
    try {
      const loginId = localStorage.getItem("login_id");
      const token = localStorage.getItem("auth_token");

      if (!loginId) {
        enqueueSnackbar("User is not authenticated.", { variant: "error" });
        return;
      }

      const response = await fetch(
        `/api/v1/orders/${order.oms_order_id}?client_id=${loginId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-authorization-token": token,
            "x-device-type": "WEB",
            "x-device-id": getDeviceId(),
          },
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        setConflictingOrders(prev => prev.filter(o => o.oms_order_id !== order.oms_order_id));
        enqueueSnackbar(data.message || "Order cancelled successfully", { variant: "success" });

        const remainingOrders = conflictingOrders.filter(o => o.oms_order_id !== order.oms_order_id);
        if (remainingOrders.length === 0) {
          setPendingOrdersModalOpen(false);
          setConflictingOrders([]);
          setCancellingOrders([]);
          setShowConfirmationInModal(false);

          if (selectedPosition.length === 1) {
            executeSquareOff();
          } else {
            setOpenConfirmationBox(true);
          }
        }
      } else {
        enqueueSnackbar(data.message || "Failed to cancel order", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to cancel order: ${error.message}`, { variant: "error" });
    } finally {
      setCancellingOrders(prev => prev.filter(id => id !== order.oms_order_id));
    }
  }, [conflictingOrders, selectedPosition, executeSquareOff, enqueueSnackbar]);

  const handleConfirmSquareOffInModal = useCallback(() => {
    setPendingOrdersModalOpen(false);
    setConflictingOrders([]);
    setCancellingOrders([]);
    setShowConfirmationInModal(false);
    executeSquareOff();
  }, [executeSquareOff]);

  const handleCancelSquareOffInModal = useCallback(() => {
    setPendingOrdersModalOpen(false);
    setConflictingOrders([]);
    setCancellingOrders([]);
    setShowConfirmationInModal(false);
  }, []);

  const handleCancelAllAndProceedSquareOffAll = useCallback(async () => {
    if (conflictingOrders.length === 0) {
      setPendingOrdersModalOpen(false);
      setConflictingOrders([]);
      setCancellingOrders([]);
      setShowConfirmationInModal(false);

      setOpenConfirmationBox(true);
      return;
    }

    try {
      const cancelPromises = conflictingOrders.map(async (order) => {
        const loginId = localStorage.getItem("login_id");
        const token = localStorage.getItem("auth_token");

        if (!loginId) {
          throw new Error("User is not authenticated.");
        }

        const response = await fetch(
          `/api/v1/orders/${order.oms_order_id}?client_id=${loginId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "x-authorization-token": token,
              "x-device-type": "WEB",
              "x-device-id": getDeviceId(),
            },
          }
        );

        const data = await response.json();
        if (data.status !== "success") {
          throw new Error(data.message || "Failed to cancel order");
        }

        return order;
      });

      await Promise.all(cancelPromises);
      enqueueSnackbar(`${conflictingOrders.length} pending orders cancelled successfully`, { variant: "success" });

      setPendingOrdersModalOpen(false);
      setConflictingOrders([]);
      setCancellingOrders([]);
      setShowConfirmationInModal(false);

      setOpenConfirmationBox(true);
    } catch (error) {
      enqueueSnackbar(`Failed to cancel orders: ${error.message}`, { variant: "error" });
    }
  }, [conflictingOrders, enqueueSnackbar]);

  const handleConvertPosition = useCallback(async (position, quantity, targetProduct) => {
    try {

      if (!position || !quantity || !targetProduct) {
        throw new Error(`Missing required parameters: ${!position ? 'position' : ''} ${!quantity ? 'quantity' : ''} ${!targetProduct ? 'targetProduct' : ''}`);
      }

      const response = await dispatch(executeConvertPositionAction(position, quantity, targetProduct));
      if (response) {
        enqueueSnackbar(response.message, { variant: "success", autoHideDuration: 1000 });
        handleCloseConversionDrawer();
        dispatch(fetchPositionsAction(positionType));
      }
    } catch (error) {
      console.error("Conversion error:", error);
      enqueueSnackbar(error || "Failed to convert position", { variant: "error", autoHideDuration: 1000 });
    }
  }, [dispatch, positionType, handleCloseConversionDrawer]);

  const handleAddMore = useCallback((position, orderType) => {
    if (!position) return;

    dispatch(openOrderWindow(mapOrderWindowData({
      ...position,
      lotsize: Math.abs(position.net_quantity),

      order_type: userSettings.order_type,
    }, orderType, "POSITION", userSettings.order_type, userSettings.product_type)));

  }, [dispatch, userSettings.order_type, userSettings.product_type]);

  useEffect(() => {
    console.log("positionMetrics", positionMetrics);
  }, [positionMetrics]);

  // useEffect(() => {
  //   if (positions.length) {
  //     let totalPNL = 0;
  //     let totalMtm = 0;
  //     let totalInvested = 0;

  //     positions.forEach(position => {
  //       // Calculate PNL
  //       const pnl = position.pnl || 0;
  //       const mtm = position.mtm || 0;
  //       totalPNL += pnl;
  //       totalMtm += mtm;
  //       // Calculate invested value for each position
  //       const investedValue = Math.abs(position.investedValue || 0);
  //       totalInvested += investedValue;
  //     });

  //     // Set total PNL
  //     setTotal(totalPNL);
  //     dispatch({
  //       type: "SET_PNL_UPDATE",
  //       payload: totalPNL
  //     });

  //     // Set total invested value
  //     // setTotalInvestedValue(totalInvested);

  //     // Calculate total PNL percentage
  //     // const pnlPercentage = totalInvested !== 0 ? (totalPNL / totalInvested) * 100 : 0;
  //     // setTotalPnlPercentage(pnlPercentage);
  //   } else {
  //     setTotal(0);
  //     setTotalInvestedValue(0);
  //     // setTotalPnlPercentage(0);
  //   }
  // }, [positions, positionsUpdate]);

  // useEffect(() => {
  //   dispatch(fetchPositionsAction(positionType));
  // }, [positionType, dispatch]);

  // useEffect(() => {
  //   const handlers = positions.map((position) => {
  //     return octopusInstance.wsHandler({
  //       messageType: "CompactMarketDataMessage",
  //       subscriptionLocation: Math.random().toString(),
  //       payload: {
  //         exchangeCode: getExchangeCode(position.exchange),
  //         instrumentToken: position.instrument_token,
  //       },
  //     });
  //   });

  //   handlers.forEach((handler, i) => {
  //     handler.subscribe(({ msg }) => {
  //       const prevPosition = { ...positions[i] };
  //       const prevLTP = prevPosition.ltp ? prevPosition.ltp : 0;
  //       const currLTP = msg.ltp;
  //       const isLTPHigh = prevLTP <= currLTP ? true : false;
  //       const position = getFormattedPosition(
  //         {
  //           ...positions[i],
  //           ltp: prevPosition.net_quantity > 0 ? msg.bidPrice : msg.askPrice,
  //           previous_close: msg.ltp - msg.absoluteChange,
  //           isLTPHigh: isLTPHigh,
  //         },
  //         prevPosition.net_quantity > 0 ? msg.bidPrice : msg.askPrice,
  //         positionType
  //       );
  //       positions.splice(i, 1, position);
  //       dispatch({
  //         type: "SET_POSITIONS_UPDATE",
  //         payload: positions,
  //       });
  //     });
  //   });

  //   return () => handlers.forEach((handler) => handler.unsubscribe());
  // }, [(positions || []).length, positions, positionType, dispatch]);

  // Add touch interaction handler for Select All
  const { handlers: selectAllHandlers, touchStates: selectAllTouchStates } =
    useTouchInteraction(handleSelectAllTap, true);

  return {
    // State
    positions,
    positionType,
    total,
    totalInvestedValue,
    totalPnlPercentage,
    isAllSelected,
    selectedPosition: selectedPosition,
    openConfirmationBox,
    conversionDrawerOpen,
    selectedPositionForConversion,
    positionsUpdate,
    pendingOrdersModalOpen,
    conflictingOrders,
    cancellingOrders,
    showConfirmationInModal,

    // Handlers
    handleSelectAllTap,
    handleCheckbox,
    handleSquareOffSingle,
    handleSquareOffClick,
    setOpenConfirmationBox,
    executeSquareOff,
    handleConvertClick,
    handleCloseConversionDrawer,
    handleConvertPosition,
    handleAddMore,
    handleCancelPendingOrder,
    handleClosePendingOrdersModal,
    handleCancelAndProceedSquareOff,
    handleConfirmSquareOffInModal,
    handleCancelSquareOffInModal,
    handleCancelAllAndProceedSquareOffAll,
    shouldDisplaySquareOff,
    isSquareOffEnabled,
    cancelConflictingOrdersForPosition,
    getAllConflictingOrdersWithScriptNames,
    shouldShowSquareOffAllModal,
    getPositionPendingOrders,
    getSelectedPositionsWithPendingOrders,

    // Touch Handlers
    selectAllHandlers,
    selectAllTouchStates,

    // Actions
    dispatch,
    onConvert: handleConvertClick,
    onAddMore: handleAddMore,
  };
}; 